/**
 * RB Classifications Mapping Constants Structure:
 *
 * These mappings define the RB Classification matching criteria based on various keywords.
 * Each entry object may contain the following keys:
 * - `matches` (Array of Arrays): Defines OR conditions within AND groups.
 *   Each sub-array within `matches` represents terms in an OR condition. Multiple sub-arrays
 *   are treated as AND groups. This means that at least one term in each sub-array must be present
 *   in the target string for a match.
 *   Example:
 *     - `matches: [['google', 'bing'], ['search', 'adwords']]`
 *       - The string must contain at least one term from `['google', 'bing']`
 *         AND one term from `['search', 'adwords']`.
 *
 * - `orGroups` (Array of Arrays): Defines AND conditions within OR groups.
 *   Each sub-array within `orGroups` requires all terms within the sub-array (AND).
 *   Multiple sub-arrays represent alternative groups (OR across groups).
 *   This means that at least one group must match, where each group requires all terms within it.
 *   Example:
 *     - `orGroups: [['tv', 'sci'], ['stack', 'adapt']]`
 *       - The string must contain both `tv` and `sci` OR both `stack` and `adapt`.
 *
 * - `exclude` (Array of Strings, optional): Excludes specific terms from the match.
 *   The `exclude` array lists terms that must NOT appear in the target string. If any
 *   term in `exclude` is found in the target string, it will not match the entry.
 *
 * - `text` (String): A descriptive label for the entry that will be used as the display name
 *   or identifier when a match is found.
 * - `key` (String): The original identifier used for this entry.
* */
// see hooks.js usePlatformMappings to how all platforms from interations API are mapped

export const PLATFORM_MAPPING = {
  Direct: {
    matches: [['direct']],
    text: 'Direct',
    key: 'direct',
  },
  Organic: {
    matches: [['referr', 'organic']],
    text: 'Organic',
    exclude: ['paid'],
    key: 'organic',
  },
  AdMedia: {
    matches: [[]],
    text: 'AdMedia',
    key: 'admedia',
  },
  Google: {
    matches: [['google']],
    text: 'Google',
    exclude: ['organic', 'youtube', 'video'],
    key: 'adwords',
  },
  Acuity: {
    matches: [['acuity']],
    text: 'Acuity',
    key: 'acuityads',
  },
  'Apple Search': {
    matches: [['apple'], ['search']],
    text: 'Apple Search',
    exclude: ['organic', 'google', 'bing'],
    key: 'apple_search_ads',
  },
  'Apple News': {
    matches: [['apple'], ['news']],
    text: 'Apple News',
    key: 'apple_news',
  },
  Attentive: {
    matches: [['attentive']],
    text: 'Attentive',
    key: 'attentive',
  },
  'Belardi Wong': {
    matches: [['belardi']],
    text: 'Belardi Wong',
    key: 'belardi_wong',
  },
  Bing: {
    matches: [[]],
    text: 'Bing',
    exclude: ['organic', 'affiliate'],
    key: 'bing',
  },
  Blisspoint: {
    matches: [['bpm', 'blisspoint']],
    text: 'Blisspoint',
    key: 'blisspoint_media',
  },
  CohereOne: {
    matches: [['cohere']],
    text: 'CohereOne',
    key: 'cohere',
  },
  'Commission Junction': {
    orGroups: [
      ['commission', 'junction'],
      ['cj', 'affiliate'],
    ],
    text: 'Commission Junction',
    key: 'commission_junction',
  },
  DV360: {
    matches: [['db360']],
    text: 'DV360',
    key: 'dbm',
  },
  'Direct Avenue': {
    matches: [['direct'], ['avenue']],
    text: 'Direct Avenue',
    key: 'direct_avenue',
  },
  Meta: {
    matches: [['instagram', 'fb\/ig', 'fb-ig', 'meta']], // eslint-disable-line no-useless-escape,
    text: 'Meta',
    exclude: ['organic', 'email'],
    key: 'facebook',
  },
  iHeartRadio: {
    matches: [['iheart']],
    text: 'iHeartRadio',
    key: 'iheartradio',
  },
  Illumin: {
    matches: [['acuity', 'illumin']],
    text: 'Illumin',
    key: 'illumin',
  },
  Impact: {
    matches: [['impact']],
    text: 'Impact',
    key: 'impactradius',
  },
  Klaviyo: {
    matches: [['klaviyo']],
    text: 'Klaviyo',
    key: 'klaviyo',
  },
  LinkedIn: {
    matches: [['linked']],
    text: 'LinkedIn',
    key: 'linkedin',
  },
  LiveIntent: {
    matches: [['live_intent', 'live intent']],
    text: 'LiveIntent',
    key: 'liveintent',
  },
  'LS Direct': {
    matches: [['ls direct', 'ls_direct', 'ls-direct']],
    text: 'LS Direct',
    key: 'ls_direct',
  },
  MailChimp: {
    matches: [['mailchimp']],
    text: 'MailChimp',
    key: 'mailchimp',
  },
  'Marketing Architects': {
    matches: [['architects']],
    text: 'Marketing Architects',
    key: 'marketing_architects',
  },
  'Media Alpha': {
    matches: [['media'], ['alpha']],
    text: 'Media Alpha',
    key: 'mediaalpha',
  },
  MNTN: {
    matches: [['mntn']],
    text: 'MNTN',
    key: 'mntn',
  },
  Nativo: {
    matches: [[]],
    text: 'Nativo',
    exclude: [''],
    key: 'nativo',
  },
  PebblePost: {
    matches: [['pebble post', 'pebble-post', 'pebble_post']],
    text: 'PebblePost',
    key: 'pebblepost',
  },
  Ascend: {
    matches: [['ascend', 'partnerize']],
    text: 'Ascend',
    key: 'pepperjam',
  },
  Pinterest: {
    matches: [[]],
    text: 'Pinterest',
    exclude: ['organic'],
    key: 'pinterest',
  },
  Podsights: {
    matches: [['podsight']],
    text: 'Podsights',
    key: 'podsights',
  },
  Podscribe: {
    matches: [['podscribe']],
    text: 'Podscribe',
    key: 'podscribe',
  },
  Poplar: {
    matches: [['poplar']],
    text: 'Poplar',
    key: 'poplar',
  },
  'Power Inbox': {
    matches: [['power'], ['inbox']],
    text: 'Power Inbox',
    key: 'powerinbox',
  },
  Rain: {
    matches: [[]],
    text: 'Rain',
    exclude: ['brain', 'grain'],
    key: 'rain',
  },
  Reddit: {
    matches: [[]],
    text: 'Reddit',
    exclude: ['organic'],
    key: 'reddit',
  },
  'RTB House': {
    matches: [['rtb']],
    text: 'RTB House',
    key: 'rtb_house',
  },
  'Share Local Media': {
    orGroups: [['slm'], ['share', 'local']],
    text: 'Share Local Media',
    key: 'slm',
  },
  Simulmedia: {
    matches: [[]],
    text: 'Simulmedia',
    exclude: [''],
    key: 'simulmedia',
  },
  Snapchat: {
    matches: [['snap']],
    text: 'Snapchat',
    exclude: ['organic'],
    key: 'snapchat',
  },
  StackAdapt: {
    orGroups: [['stackadapt'], ['stack', 'adapt']],
    text: 'StackAdapt',
    key: 'stackadapt',
  },
  'The Trade Desk': {
    orGroups: [['ttd'], ['trade', 'desk']],
    text: 'The Trade Desk',
    key: 'the_trade_desk',
  },
  TikTok: {
    matches: [[]],
    text: 'TikTok',
    exclude: ['organic', 'affiliate'],
    key: 'tiktok',
  },
  'TV Scientific': {
    matches: [['tvsci', 'scientific']],
    orGroups: [['tv', 'sci']],
    text: 'TV Scientific',
    key: 'tvscientific',
  },
  Xandr: {
    matches: [['appnexus']],
    text: 'Xandr',
    key: 'xandr',
  },
  Verizon: {
    orGroups: [['yahoo', 'verizon', 'oath']],
    text: 'Verizon',
    exclude: ['organic'],
    key: 'verizon',
  },
  YouTube: {
    matches: [['youtube', 'google', 'adwords'], ['video']],
    text: 'YouTube',
    exclude: ['organic'],
    key: 'youtube',
  },
  'Zeta Global': {
    matches: [['zeta']],
    text: 'Zeta Global',
    key: 'zetaglobal',
  },
  DCM: {
    matches: [['dcm']],
    text: 'DCM',
    key: 'dcm',
  },
  CausalIQ: {
    matches: [['causaliq']],
    text: 'CausalIQ',
    key: 'causaliq',
  },
  VDX: {
    matches: [['vdx']],
    text: 'VDX',
    key: 'vdx',
  },
  Amazon: {
    matches: [['amazon']],
    text: 'Amazon',
    key: 'amazon',
  },
  Unclassified: {
    matches: [[]],
    text: 'Unclassified',
    key: 'unclassified',
  },
};

export const AD_TYPE_MAPPING = {
  PMAX: {
    matches: [['google', 'bing', 'adwords'], ['pmax', 'performance', 'shopping']],
    text: 'PMAX',
    exclude: ['organic'],
    key: 'pmax',
  },
  'Non Brand Search': {
    matches: [['search', 'google', 'adwords', 'bing'], ['amazon', 'non', 'nb', 'generic']],
    text: 'Non Brand Search',
    exclude: ['organic'],
    key: 'nonbrand',
  },
  'Brand Search': {
    matches: [['search', 'google', 'adwords', 'bing', 'amazon'], ['brand']],
    text: 'Brand Search',
    exclude: ['organic'],
    key: 'brand',
  },
  'Advantage+': {
    matches: [['meta', 'facebook', 'fb'], ['advantage', 'adv']],
    text: 'Advantage+',
    exclude: ['organic'],
    key: 'advantageplus',
  },
  'Coupon Affiliates': {
    matches: [
      ['affiliate', 'impact', 'pepperjam', 'partnerize', 'commission%'],
      ['coupon', 'deals', 'rebate', 'cashback', 'offers', 'discount', 'promo', 'retailmenot', 'rakuten', 'slickdeals', 'topcashback'],
    ],
    text: 'Coupon Affiliates',
    exclude: ['organic'],
    key: 'couponaffiliates',
  },
  Direct: {
    matches: [['direct']],
    text: 'Direct',
    key: 'direct',
  },
  Unclassified: {
    matches: [[]],
    text: 'Unclassified',
    key: 'unclassified',
  },
};

export const AUDIENCE_TARGETING_MAPPING = {
  Prospecting: {
    matches: [['pros', 'cold']],
    text: 'Prospecting',
    exclude: ['refer'],
    key: 'prospecting',
  },
  Retargeting: {
    matches: [['rtg', 'retarg', 'abandon', 'warm', 'remarket', 'rmkt']],
    text: 'Retargeting',
    exclude: ['refer'],
    key: 'retargeting',
  },
  Retention: {
    matches: [['rtn', 'retention']],
    text: 'Retention',
    exclude: ['partner'],
    key: 'retention',
  },
  Awareness: {
    matches: [['awareness']],
    text: 'Awareness',
    key: 'awareness',
  },
  Consideration: {
    matches: [['consideration']],
    text: 'Consideration',
    key: 'consideration',
  },
  Conversion: {
    matches: [['conversion']],
    text: 'Conversion',
    key: 'conversion',
  },
  Acquisition: {
    matches: [['acquisition']],
    text: 'Acquisition',
    key: 'acquisition',
  },
  Direct: {
    matches: [['direct']],
    text: 'Direct',
    key: 'direct',
  },
  Unclassified: {
    matches: [[]],
    text: 'Unclassified',
    key: 'unclassified',
  },
};

export const CHANNEL_MAPPING = {
  Referral: {
    matches: [['refer']],
    text: 'Referral',
    exclude: ['email', 'sms', 'affiliate'],
    key: 'referral',
  },
  'Paid Social': {
    matches: [['facebook', 'meta', 'tiktok', 'snap', 'pinterest', 'reddit', 'twitter', 'linkedin', 'social']],
    text: 'Paid Social',
    exclude: ['organic', 'email'],
    key: 'paidsocial',
  },
  'Streaming CTV OTT': {
    matches: [['ctv', 'ott', 'streaming', 'roku']],
    text: 'Streaming CTV OTT',
    exclude: ['audio', 'display', 'bottom', 'email', 'sms', 'influencer', 'refer', 'affiliate'],
    key: 'streaming',
  },
  'Paid Video': {
    matches: [['video', 'youtube', 'streaming']],
    text: 'Paid Video',
    exclude: ['organic', 'email', 'tatari', 'audio'],
    key: 'paidvideo',
  },
  Display: {
    matches: [['display', 'programmatic', 'criteo', 'verizon', 'yahoo', 'stackadapt', 'gdn', 'dsp', 'liveintent']],
    text: 'Display',
    exclude: ['organic', 'refer', 'affiliate', 'video'],
    key: 'display',
  },
  'Linear TV': {
    matches: [['tv', 'linear', 'television']],
    text: 'Linear TV',
    exclude: ['streaming', 'ott', 'ctv', 'video', 'email'],
    key: 'lineartv',
  },
  PMAX: {
    matches: [['pmax', 'shopping', 'performance max', 'performance_max']],
    text: 'PMAX',
    exclude: ['organic', 'affiliate'],
    key: 'pmax',
  },
  Native: {
    matches: [['native', 'outbrain', 'taboola', 'triplelift']],
    text: 'Native',
    exclude: ['organic'],
    key: 'native',
  },
  'Paid Search': {
    matches: [['google', 'bing', 'search']],
    text: 'Paid Search',
    exclude: ['organic', 'affiliate'],
    key: 'paidsearch',
  },
  Affiliate: {
    matches: [['affiliate', 'impact', 'pepperjam', 'partnerize', 'commission']],
    text: 'Affiliate',
    key: 'affiliate',
  },
  Email: {
    matches: [['email']],
    text: 'Email',
    exclude: ['direct mail', 'sms', 'referring'],
    key: 'email',
  },
  'Direct Mail': {
    matches: [['mail', 'dm_', 'print', 'postpilot', 'slm', 'merkle', 'pebblepost', 'belardi', 'postie']],
    text: 'Direct Mail',
    exclude: ['email', 'facebook', 'google'],
    key: 'directmail',
  },
  Podcast: {
    matches: [['podcast', 'podsight', 'podscribe']],
    text: 'Podcast',
    exclude: ['audio', 'radio'],
    key: 'podcast',
  },
  OOH: {
    matches: [['ooh', 'out of home']],
    text: 'OOH',
    key: 'ooh',
  },
  Amazon: {
    matches: [['amazon']],
    text: 'Amazon',
    exclude: ['refer'],
    key: 'amazon',
  },
  Audio: {
    matches: [['audio', 'radio', 'pandora']],
    text: 'Audio',
    exclude: ['podcast'],
    key: 'audio',
  },
  Influencer: {
    matches: [['influencer']],
    text: 'Influencer',
    key: 'influencer',
  },
  SMS: {
    matches: [['sms', 'text']],
    text: 'SMS',
    exclude: ['email'],
    key: 'sms',
  },
  Organic: {
    matches: [['organic']],
    text: 'Organic',
    exclude: ['paid'],
    key: 'organic',
  },
  PR: {
    matches: [['press', 'pressrelease', 'press_release']],
    text: 'PR',
    key: 'pr',
  },
  Direct: {
    matches: [['direct']],
    text: 'Direct',
    key: 'direct',
  },
  Unclassified: {
    matches: [[]],
    text: 'Unclassified',
    key: 'unclassified',
  },
};
